import { useState, useEffect } from 'react'
import { useApiQuery } from '../../../shared/hooks'
import { TrendDataPoint } from '../../../../../shared/types'
import { LoadingSpinner } from '../../../shared/components'
import './TrendChart.css'

interface TrendChartProps {
  className?: string
}

interface ChartBarData {
  month: string
  value: number
  displayValue: string
  height: number
}

export default function TrendChart({ className = '' }: TrendChartProps) {
  const [chartData, setChartData] = useState<ChartBarData[]>([])
  
  const { 
    data: trendData, 
    isLoading, 
    error 
  } = useApiQuery<TrendDataPoint[]>(
    ['dashboard', 'trends'],
    '/dashboard/trends',
    {
      staleTime: 60000, // Consider data stale after 1 minute
    }
  )

  useEffect(() => {
    if (trendData && trendData.length > 0) {
      // Find the maximum net worth to calculate percentage heights
      const maxNetWorth = Math.max(...trendData.map(point => Math.abs(point.netWorth)))
      const minHeight = 20 // Minimum height percentage for visibility
      
      const processedData: ChartBarData[] = trendData.map(point => {
        const date = new Date(point.date)
        const month = date.toLocaleDateString('en-US', { month: 'short' })
        
        // Calculate height as percentage (20% minimum for visibility)
        const heightPercentage = maxNetWorth > 0 
          ? Math.max(minHeight, (Math.abs(point.netWorth) / maxNetWorth) * 80 + 20)
          : minHeight
        
        // Format display value (e.g., "$286k")
        const displayValue = formatCurrency(point.netWorth, true)
        
        return {
          month,
          value: point.netWorth,
          displayValue,
          height: heightPercentage
        }
      })
      
      setChartData(processedData)
    }
  }, [trendData])

  const formatCurrency = (amount: number, abbreviated = false): string => {
    if (abbreviated) {
      if (Math.abs(amount) >= 1000000) {
        return `$${(amount / 1000000).toFixed(1)}M`
      } else if (Math.abs(amount) >= 1000) {
        return `$${Math.round(amount / 1000)}k`
      }
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  if (isLoading) {
    return (
      <div className={`trend-chart-container ${className}`}>
        <div className="chart-loading">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`trend-chart-container ${className}`}>
        <div className="chart-error">
          <p>Failed to load trend data: {error.message}</p>
        </div>
      </div>
    )
  }

  if (!chartData.length) {
    return (
      <div className={`trend-chart-container ${className}`}>
        <div className="chart-empty">
          <p>No trend data available</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`trend-chart-container ${className}`}>
      <div className="chart-container">
        {chartData.map((bar, index) => (
          <div
            key={`${bar.month}-${index}`}
            className="chart-bar"
            style={{ height: `${bar.height}%` }}
          >
            <div className="bar-value">{bar.displayValue}</div>
            <div className="bar-label">{bar.month}</div>
          </div>
        ))}
      </div>
    </div>
  )
}